import { captableUrl, fundOwnershipUrl, valuationUrl } from 'common/config/urls';
import { CAP_TABLE_SLUG, CAP_TABLE_TITLE, FUND_OWNERSHIP_SLUG, FUND_OWNERSHIP_TITLE } from 'common/constants/cap-table';
import { COMPANY_ACTION_QUERY_PARAM, COMPANY_PAGE_ACTIONS, type CompanyPageAction } from 'common/constants/pageActions';
import { VALUATIONS_SLUG, VALUATIONS_TITLE } from 'common/constants/valuations';
import {
  CALIBRATION_MODEL_NAME,
  RANGE_SENSITIVITY_MODEL_NAME,
  VALUATIONS_FUTURE_EXIT_APPROACH,
  VALUATIONS_SECONDARY_TRANSACTIONS_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';

export interface SuperSearchCompanyCommandDefinition {
  action: CompanyPageAction;
  aliases: readonly string[];
  hostPage: {
    key: string;
    label: string;
    path: (firmSlug: string, companySlug: string) => string;
  };
  key: string;
  label: string;
  permission: 'company-mutation' | 'pro-forma';
}

const withCompanyAction = (path: string, action: CompanyPageAction) => {
  const searchParams = new URLSearchParams({ [COMPANY_ACTION_QUERY_PARAM]: action });
  return `${path}?${searchParams.toString()}`;
};

const capTableHost = {
  key: CAP_TABLE_SLUG,
  label: CAP_TABLE_TITLE,
  path: captableUrl,
};
const fundOwnershipHost = {
  key: FUND_OWNERSHIP_SLUG,
  label: FUND_OWNERSHIP_TITLE,
  path: fundOwnershipUrl,
};
const valuationsHost = {
  key: VALUATIONS_SLUG,
  label: VALUATIONS_TITLE,
  path: valuationUrl,
};

export const SUPER_SEARCH_COMPANY_COMMANDS: readonly SuperSearchCompanyCommandDefinition[] = [
  {
    action: COMPANY_PAGE_ACTIONS.CREATE_PRO_FORMA_CAP_TABLE,
    aliases: ['clone cap table', 'new proforma', 'pro forma version'],
    hostPage: capTableHost,
    key: 'create-pro-forma-cap-table',
    label: 'Create Pro Forma Cap Table Version',
    permission: 'pro-forma',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_SECURITY,
    aliases: ['create security', 'new security', 'security class'],
    hostPage: capTableHost,
    key: 'add-security',
    label: 'Add Security',
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_FUND_OWNERSHIP,
    aliases: ['create fund ownership', 'new fund ownership', 'ownership'],
    hostPage: fundOwnershipHost,
    key: 'add-fund-ownership',
    label: 'Add Fund Ownership',
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_RANGE_SENSITIVITY,
    aliases: ['range sensitivity', 'sensitivity analysis'],
    hostPage: valuationsHost,
    key: 'add-range-sensitivity',
    label: `Add ${RANGE_SENSITIVITY_MODEL_NAME}`,
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_BACKSOLVE_APPROACH,
    aliases: ['backsolve valuation'],
    hostPage: valuationsHost,
    key: 'add-backsolve-approach',
    label: 'Add Backsolve Approach',
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_SECONDARY_TRANSACTIONS_APPROACH,
    aliases: ['secondary transaction', 'secondary transactions'],
    hostPage: valuationsHost,
    key: 'add-secondary-transactions-approach',
    label: `Add ${VALUATIONS_SECONDARY_TRANSACTIONS_APPROACH} Approach`,
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_DCF_APPROACH,
    aliases: ['discounted cash flow', 'dcf'],
    hostPage: valuationsHost,
    key: 'add-dcf-approach',
    label: 'Add DCF Approach',
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_EXTERNAL_VALUATION_APPROACH,
    aliases: ['external valuation'],
    hostPage: valuationsHost,
    key: 'add-external-valuation-approach',
    label: 'Add External Valuation Approach',
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_FUTURE_EXIT_APPROACH,
    aliases: ['exit scenario', 'future exit'],
    hostPage: valuationsHost,
    key: 'add-future-exit-approach',
    label: `Add ${VALUATIONS_FUTURE_EXIT_APPROACH} Approach`,
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_PUBLIC_COMPS_APPROACH,
    aliases: ['guideline public companies', 'gpc', 'public comps'],
    hostPage: valuationsHost,
    key: 'add-public-comps-approach',
    label: 'Add Public Comps Approach',
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_TRANSACTION_COMPS_APPROACH,
    aliases: ['guideline public transactions', 'gpt', 'transaction comps'],
    hostPage: valuationsHost,
    key: 'add-transaction-comps-approach',
    label: 'Add Transaction Comps Approach',
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_SPECIFIED_SHARE_VALUES_APPROACH,
    aliases: ['specified share values', 'ssv'],
    hostPage: valuationsHost,
    key: 'add-specified-share-values-approach',
    label: 'Add Specified Share Values Approach',
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_CALIBRATION,
    aliases: ['calibration', 'guideline calibration'],
    hostPage: valuationsHost,
    key: 'add-calibration',
    label: `Add ${CALIBRATION_MODEL_NAME}`,
    permission: 'company-mutation',
  },
  {
    action: COMPANY_PAGE_ACTIONS.ADD_ALLOCATION_SCENARIO,
    aliases: ['add scenario', 'allocation scenario', 'equity allocation scenario'],
    hostPage: valuationsHost,
    key: 'add-allocation-scenario',
    label: 'Add Allocation Scenario',
    permission: 'company-mutation',
  },
];

export const getCompanyCommandPath = (
  definition: SuperSearchCompanyCommandDefinition,
  firmSlug: string,
  companySlug: string
) => withCompanyAction(definition.hostPage.path(firmSlug, companySlug), definition.action);
